import { z } from 'zod'

import { fmtDate, presencaRows, type AtaParticipanteInput, type AtaPresencaRow } from './ata-model'
import { weekLabel } from './iso-week'

/**
 * ATA de Reunião de Alinhamento Semanal (RAS) — modelo da Flexibase:
 *
 *   1 informações · 2 objetivo · 3 pauta/assuntos tratados ·
 *   4 objetivos da semana · 5 ações individuais · 6 ações conjuntas ·
 *   7 comunicações/observações
 *
 * `RasConteudoSchema` é o formato de `supply.ata.conteudo` (tipo 'ras');
 * `buildRasModel` monta os dados que o PDF desenha e que ficam congelados no
 * snapshot quando a ATA fecha.
 */

export const RAS_OBJETIVO =
  'Promover o alinhamento semanal da equipe, acompanhar demandas e resultados, identificar desvios, definir prioridades e estabelecer responsáveis e prazos para as ações necessárias.'

export const RAS_FOOTER = 'ATA RAS - Flexibase'

/** Textos de Configurações → Modelos de ATA; sem eles, os padrões acima. */
export interface RasTexts {
  readonly company?: string
  readonly objetivo?: string
  readonly footer?: string
  readonly docCode?: string
  readonly system?: string
}

/** O modelo impresso traz 4 linhas numeradas por tabela; o PDF completa até 4. */
export const RAS_MIN_ROWS = 4

const text = (max: number) => z.string().max(max).default('')

export const RasPautaItemSchema = z.object({
  assunto: text(500),
  situacao: text(1000),
  definicao: text(1000),
})

export const RasObjetivoItemSchema = z.object({
  objetivo: text(500),
})

export const RasAcaoItemSchema = z.object({
  /** Pessoa do sistema (opcional) — o nome fica em `responsavel` para o PDF. */
  responsavelId: z.string().uuid().nullable().default(null),
  responsavel: text(200),
  acao: text(1000),
  definicao: text(1000),
})

export const RasConteudoSchema = z.object({
  pauta: z.array(RasPautaItemSchema).max(50).default([]),
  objetivos: z.array(RasObjetivoItemSchema).max(50).default([]),
  acoesIndividuais: z.array(RasAcaoItemSchema).max(50).default([]),
  acoesConjuntas: z.array(RasAcaoItemSchema).max(50).default([]),
  comunicacoes: text(4000),
})

export type RasPautaItem = z.infer<typeof RasPautaItemSchema>
export type RasObjetivoItem = z.infer<typeof RasObjetivoItemSchema>
export type RasAcaoItem = z.infer<typeof RasAcaoItemSchema>
export type RasConteudo = z.infer<typeof RasConteudoSchema>

/** Lê o jsonb do banco com tolerância: campo ausente ou inválido vira vazio. */
export function parseRasConteudo(raw: unknown): RasConteudo {
  const parsed = RasConteudoSchema.safeParse(raw ?? {})
  return parsed.success ? parsed.data : RasConteudoSchema.parse({})
}

const filled = (values: readonly string[]) => values.some((v) => v.trim())

/** Remove linhas totalmente vazias antes de gravar. */
export function compactRasConteudo(c: RasConteudo): RasConteudo {
  return {
    pauta: c.pauta.filter((p) => filled([p.assunto, p.situacao, p.definicao])),
    objetivos: c.objetivos.filter((o) => filled([o.objetivo])),
    acoesIndividuais: c.acoesIndividuais.filter((a) => filled([a.responsavel, a.acao, a.definicao])),
    acoesConjuntas: c.acoesConjuntas.filter((a) => filled([a.responsavel, a.acao, a.definicao])),
    comunicacoes: c.comunicacoes.trim(),
  }
}

export interface RasInfoInput {
  readonly semana: string
  readonly areaName: string | null
  readonly data: string | null
  readonly inicio: string | null
  readonly fim: string | null
  readonly local: string | null
  readonly respReuniao: string | null
  readonly respAta: string | null
  /** Convocados e presença (fica fora quando a tela não manda). */
  readonly participantes?: readonly AtaParticipanteInput[]
}

export interface RasModel {
  readonly title: 'ATA DE REUNIÃO DE ALINHAMENTO SEMANAL – RAS'
  readonly company: string
  /** Código do formulário e revisão ("R02 · Rev. 01"); ausente em snapshots antigos. */
  readonly docCode?: string
  /** Nome do sistema (rodapé de cada página); ausente em snapshots antigos. */
  readonly system?: string
  readonly weekLabel: string
  readonly info: readonly (readonly [string, string, string, string])[]
  /** Lista de presença; ausente em snapshots fechados antes dela existir. */
  readonly presencas?: readonly AtaPresencaRow[]
  readonly objetivo: string
  readonly pauta: readonly (readonly [string, string, string, string])[]
  readonly objetivos: readonly (readonly [string, string])[]
  readonly acoesIndividuais: readonly (readonly [string, string, string, string])[]
  readonly acoesConjuntas: readonly (readonly [string, string, string, string])[]
  readonly comunicacoes: string
  readonly footer: string
}

const nn = (i: number) => String(i + 1).padStart(2, '0')

function pad<T extends readonly string[]>(rows: T[], width: number): T[] {
  const out = [...rows]
  while (out.length < RAS_MIN_ROWS) out.push([nn(out.length), ...Array(width - 1).fill('')] as unknown as T)
  return out
}

export function buildRasModel(info: RasInfoInput, conteudo: RasConteudo, texts: RasTexts = {}): RasModel {
  const c = compactRasConteudo(conteudo)
  const acoes = (list: RasAcaoItem[]) =>
    pad(
      list.map((a, i) => [nn(i), a.responsavel.trim(), a.acao.trim(), a.definicao.trim()] as const),
      4,
    )
  return {
    title: 'ATA DE REUNIÃO DE ALINHAMENTO SEMANAL – RAS',
    company: texts.company || 'FLEXIBASE',
    docCode: texts.docCode ?? '',
    system: texts.system ?? '',
    weekLabel: weekLabel(info.semana),
    info: [
      ['Data', fmtDate(info.data), 'Horário de início', info.inicio ?? ''],
      ['Horário de término', info.fim ?? '', 'Local / Plataforma', info.local ?? ''],
      ['Área', info.areaName ?? 'GERAL (todas as áreas)', 'Responsável pela reunião', info.respReuniao ?? ''],
      ['Responsável pela ata', info.respAta ?? '', 'Semana', weekLabel(info.semana).replace(/^Semana /, '')],
    ],
    presencas: presencaRows(info.participantes ?? []),
    objetivo: texts.objetivo || RAS_OBJETIVO,
    pauta: pad(
      c.pauta.map((p, i) => [nn(i), p.assunto.trim(), p.situacao.trim(), p.definicao.trim()] as const),
      4,
    ),
    objetivos: pad(
      c.objetivos.map((o, i) => [nn(i), o.objetivo.trim()] as const),
      2,
    ),
    acoesIndividuais: acoes(c.acoesIndividuais),
    acoesConjuntas: acoes(c.acoesConjuntas),
    comunicacoes: c.comunicacoes,
    footer: texts.footer ?? RAS_FOOTER,
  }
}
